import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Modal, Button, TextField } from "@mui/material";
import Cookies from "js-cookie";

export default function Events() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false); // State for image modal
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [imageUrls, setImageUrls] = useState([]); // State for image URLs
  const [descriptionUrl, setDescriptionUrl] = useState(""); // State for description MDX URL
  const [showCreateEventModal, setShowCreateEventModal] = useState(false); // State for Create Product modal
  const [newEvent, setNewEvent] = useState({
    eventId: "",
    title: "",
    description: "",
    eventImagesUrls: [""],
  });

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    { field: "eventId", headerName: "Event ID", width: 130 },
    { field: "title", headerName: "Title", width: 200 },
    { field: "description", headerName: "Description", width: 300 },
    { field: "eventImagesUrls", headerName: "Image URLs", width: 200 },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <div className="inline-flex space-x-1 items-center h-full">
          <button
            className="text-sm px-3 py-1 font-semibold tracking-wide bg-[#121212] hover:bg-[#000000] text-white rounded-md"
            onClick={() => handleEditClick(params.row.eventId)}
          >
            Edit
          </button>
        </div>
      ),
    },
  ];

  const handleCreateEventClick = () => {
    setShowCreateEventModal(true);
  };

  const handleEditClick = (eventId) => {
    const eventToEdit = rows.find((event) => event.eventId === eventId);
    setSelectedEvent(eventToEdit);

    // Ensure eventImagesUrls is properly split into an array of URLs
    const images =
      eventToEdit?.eventImagesUrls &&
      typeof eventToEdit.eventImagesUrls === "string"
        ? eventToEdit.eventImagesUrls.split(",").map((url) => url.trim()) // Split by comma
        : eventToEdit?.eventImagesUrls || []; // Use existing array or default to empty

    setImageUrls(images);

    setDescriptionUrl(eventToEdit?.description || "");
  };

  const handleCreateEventChange = (event) => {
    const { name, value } = event.target;
    setNewEvent((prevEvent) => ({ ...prevEvent, [name]: value }));
  };

  const handleSaveNewEvent = async () => {
    try {
      const adminToken = Cookies.get("adminKey");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}events/createEvent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Admin-Token": adminToken,
          },
          body: JSON.stringify(newEvent),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setShowCreateEventModal(false);
      setNewEvent({
        eventId: "",
        title: "",
        description: [""],
        eventImagesUrls: [""],
      }); // Reset form
      // Refresh the data
      // ... (Your code to refresh the events data)
    } catch (error) {
      console.error("Error creating event:", error);
    }
  };

  const handleSaveEvent = async () => {
    try {
      const adminToken = Cookies.get("adminKey");

      if (!selectedEvent) {
        console.error("No selected event to save");
        return;
      }

      // Make sure that both the image URLs and description URL are up-to-date
      const updatedEvent = {
        ...selectedEvent,
        eventImagesUrls: imageUrls.filter((url) => url.trim() !== ""),
        description: descriptionUrl.trim(),
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URI}events/updateEvent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Admin-Token": adminToken,
          },
          body: JSON.stringify(updatedEvent),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to save product changes");
      }

      // Update rows in the UI
      const updatedRows = rows.map((event) =>
        event.eventId === selectedEvent.eventId ? updatedEvent : event,
      );
      setRows(updatedRows);

      // Close modal
      handleCloseModal();
    } catch (error) {
      console.error("Error saving product:", error);
    }
  };

  const handleCloseCreateEventModal = () => {
    setShowCreateEventModal(false);
    setNewEvent({
      eventId: "",
      title: "",
      description: "",
      eventImagesUrls: [""],
    });
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
  };

  const handleEditImagesClick = () => {
    setShowImageModal(true);
  };

  const handleEditDescriptionClick = () => {
    setShowDescriptionModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
  };

  const handleCloseDescriptionModal = () => {
    setShowDescriptionModal(false);
  };

  const handleImageChange = (index, event) => {
    const newUrls = [...imageUrls];
    newUrls[index] = event.target.value; // Update the URL at the specified index
    setImageUrls(newUrls);
  };

  const handleSaveImages = () => {
    setSelectedEvent({
      ...selectedEvent,
      eventImagesUrls: imageUrls.filter((url) => url !== ""),
    });
    handleCloseImageModal();
  };

  const handleSaveDescription = () => {
    setSelectedEvent({ ...selectedEvent, description: descriptionUrl.trim() });
    handleCloseDescriptionModal();
  };

  useEffect(() => {
    async function fetchData() {
      try {
        const adminToken = Cookies.get("adminKey");
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URI}events/getEvents`,
          {
            method: "GET",
            headers: {
              "Admin-Token": adminToken,
            },
          },
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();

        const formattedRows = data.map((event) => ({
          id: event.id,
          eventId: event.eventId,
          title: event.title,
          description:
            typeof event.description === "string" ? event.description : "", // MDX URL string
          eventImagesUrls: Array.isArray(event.eventImagesUrls)
            ? event.eventImagesUrls.join(", ")
            : "", // Handle null or undefined image URLs
        }));

        // Set the product rows
        setRows(formattedRows);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    }

    fetchData();
  }, []); // Empty dependency to run this only once when the component mounts

  return (
    <section className="w-full mt-1">
      <div className="w-full flex justify-between pr-6 items-center">
        <h1 className="text-2xl font-bold mb-3">Tutti gli Eventi</h1>
        <button
          className="text-sm px-3 py-1 h-[50%] font-semibold tracking-wide bg-[#121212] hover:bg-[#000000] text-white rounded-md"
          onClick={handleCreateEventClick}
        >
          Crea Evento
        </button>
      </div>
      <div className="max-w-[104rem]">
        <Box sx={{ height: 775, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 13,
                },
              },
              sorting: {
                sortModel: [
                  {
                    field: "sku",
                    sort: "asc",
                  },
                ],
              },
            }}
            pageSizeOptions={[5]}
            checkboxSelection
            disableRowSelectionOnClick
          />
        </Box>
      </div>
      <Modal open={Boolean(selectedEvent)} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
          }}
        >
          <h2 className="text-lg font-bold mb-4">Edit Event</h2>
          {selectedEvent && (
            <div className="grid grid-cols-2 gap-4">
              <TextField
                label="Event ID"
                fullWidth
                value={selectedEvent.eventId}
                onChange={(e) =>
                  setSelectedEvent({
                    ...selectedEvent,
                    eventId: e.target.value,
                  })
                }
              />
              <TextField
                label="Title"
                fullWidth
                value={selectedEvent.title}
                onChange={(e) =>
                  setSelectedEvent({ ...selectedEvent, title: e.target.value })
                }
              />
            </div>
          )}
          <div className="flex justify-between w-full mt-4">
            <button
              className="bg-gray-500 text-white px-4 py-2 rounded-md"
              onClick={handleCloseModal}
            >
              Cancel
            </button>
            <div className="inline-flex space-x-2">
              <button
                className="bg-blue-500 text-white px-4 py-2 rounded-md"
                onClick={handleEditImagesClick}
              >
                Edit Images
              </button>
              <button
                className="bg-blue-500 text-white px-4 py-2 rounded-md"
                onClick={handleEditDescriptionClick}
              >
                Edit Description URL
              </button>
            </div>
            <button
              className="bg-red-500 text-white px-4 py-2 rounded-md"
              onClick={handleSaveEvent}
            >
              Save
            </button>
          </div>
        </Box>
      </Modal>

      <Modal open={showImageModal} onClose={handleCloseImageModal}>
        <Box sx={{ ...modalBoxStyles, width: 600 }}>
          <h2 className="text-lg font-bold mb-4">Edit Image URLs</h2>
          <div className="grid grid-cols-2 gap-2">
            {[...Array(20)].map((_, index) => (
              <TextField
                key={index}
                label={`Image URL ${index + 1}`}
                fullWidth
                value={imageUrls[index] || ""}
                onChange={(e) => handleImageChange(index, e)}
              />
            ))}
          </div>
          <div className="flex justify-end mt-4">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveImages}
            >
              Save Images
            </Button>
          </div>
        </Box>
      </Modal>

      <Modal open={showDescriptionModal} onClose={handleCloseDescriptionModal}>
        <Box sx={{ ...modalBoxStyles, width: 600 }}>
          <h2 className="text-lg font-bold mb-4">Edit Description MDX URL</h2>
          <TextField
            label="Description MDX URL"
            fullWidth
            value={descriptionUrl}
            onChange={(e) => setDescriptionUrl(e.target.value)}
            placeholder="https://example.com/events/my-event.mdx"
          />
          <div className="flex justify-end mt-4">
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveDescription}
            >
              Save Description
            </Button>
          </div>
        </Box>
      </Modal>

      <Modal open={showCreateEventModal} onClose={handleCloseCreateEventModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
          }}
        >
          <h2 className="text-lg font-bold mb-4">Create New Product</h2>
          <div className="grid grid-cols-2 gap-4">
            <TextField
              label="Event Id"
              fullWidth
              name="eventId"
              value={newEvent.eventId}
              onChange={handleCreateEventChange}
            />
            <TextField
              label="Title"
              fullWidth
              name="title"
              value={newEvent.title}
              onChange={handleCreateEventChange}
            />
          </div>
          <div className="mt-4 flex justify-between">
            <Button onClick={handleCloseCreateEventModal}>Cancel</Button>
            <Button onClick={handleSaveNewEvent}>Save</Button>
          </div>
        </Box>
      </Modal>
    </section>
  );
}

const modalBoxStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};
